import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Loader2 } from "lucide-react";

import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader } from "@/components/ui/Card";
import { hubModalOverlayProps } from "@/components/ui/HubModalOverlay";
import { VoiceToTextMicButton } from "@/components/ui/VoiceToTextMicButton";

import {
  isPcbSku,
  pcbIqcCheckInstruction,
  pcbIqcRepackDirection,
  validatePcbPanelSplit,
} from "@integrator-src/api/shipping-inbound/pcb-panel-iqc";

import { InboundIqcWikiPanel } from "./InboundIqcWikiPanel";
import {
  deriveIqcStatus,
  formatIqcSummary,
  iqcQuantityFieldsFromNumbers,
  type InboundIqcQuantityFields,
  validateIqcQuantities,
} from "./inboundIqcQuantities";
import type { IqcStatus } from "./inboundApi";
import { inboundIqcChipClasses } from "./InboundIqcStatusPicker";
import { cn } from "@/lib/utils";

export type InboundIqcSessionSavePayload = {
  iqc_status: IqcStatus;
  iqc_notes: string;
  iqc_passed_quantity: string;
  iqc_failed_quantity: string;
  iqc_skipped_quantity: string;
  pcb_full_panel_quantity: string;
  pcb_partial_panel_quantity: string;
  pcb_partial_panel_count: string;
};

export function InboundIqcSessionModal({
  open,
  sku,
  description,
  countedQuantity,
  initialNotes,
  initialQuantities,
  saving = false,
  printerReady = true,
  labelsSaved = false,
  onSave,
  onCancel,
}: {
  open: boolean;
  sku: string;
  description: string | null;
  countedQuantity: number;
  initialNotes: string;
  initialQuantities: InboundIqcQuantityFields;
  saving?: boolean;
  printerReady?: boolean;
  /** IQC is already stored; the next action only prints container labels. */
  labelsSaved?: boolean;
  onSave: (payload: InboundIqcSessionSavePayload) => void;
  onCancel: () => void;
}): JSX.Element | null {
  const pcbLine = isPcbSku(sku);
  const [passed, setPassed] = useState("");
  const [failed, setFailed] = useState("");
  const [skipped, setSkipped] = useState("");
  const [fullPanels, setFullPanels] = useState("");
  const [partialPanels, setPartialPanels] = useState("");
  const [partialPanelCount, setPartialPanelCount] = useState("");
  const [notes, setNotes] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setPassed(initialQuantities.iqc_passed_quantity);
    setFailed(initialQuantities.iqc_failed_quantity);
    setSkipped(initialQuantities.iqc_skipped_quantity);
    setFullPanels("");
    setPartialPanels("");
    setPartialPanelCount("");
    setNotes(initialNotes);
    setError(null);
  }, [initialNotes, initialQuantities, open]);

  if (!open) return null;

  const fields: InboundIqcQuantityFields = {
    iqc_passed_quantity: passed,
    iqc_failed_quantity: failed,
    iqc_skipped_quantity: skipped,
  };
  const previewStatus = deriveIqcStatus(countedQuantity, fields);
  const previewSummary = formatIqcSummary(fields);

  const pcbFull = fullPanels.trim() === "" ? null : Number.parseInt(fullPanels.trim(), 10);
  const pcbPartial = partialPanels.trim() === "" ? null : Number.parseInt(partialPanels.trim(), 10);
  const pcbCount = partialPanelCount.trim() === "" ? null : Number.parseInt(partialPanelCount.trim(), 10);
  const pcbDirection =
    pcbFull != null && pcbPartial != null && Number.isInteger(pcbFull) && Number.isInteger(pcbPartial)
      ? pcbIqcRepackDirection(pcbFull, pcbPartial)
      : "Repack so partial panels do not share a container with full panels. Saving prints a container label for each repack container, marked FULL PANEL or PARTIAL PANEL.";

  const handleSave = (): void => {
    if (pcbLine) {
      if (!printerReady) {
        setError("Select a 4x2 label printer in the header.");
        return;
      }
      const split = validatePcbPanelSplit(countedQuantity, {
        fullPanelBoards: Number.isInteger(pcbFull) ? pcbFull : null,
        partialPanelBoards: Number.isInteger(pcbPartial) ? pcbPartial : null,
        partialPanelCount: partialPanels.trim() === "" || pcbPartial === 0
          ? (partialPanelCount.trim() === "" ? null : (Number.isInteger(pcbCount) ? pcbCount : null))
          : (Number.isInteger(pcbCount) ? pcbCount : null),
      });
      if (!split.ok) {
        setError(split.message);
        return;
      }
      setError(null);
      onSave({
        iqc_status: "passed",
        iqc_notes: notes.trim(),
        iqc_passed_quantity: String(countedQuantity),
        iqc_failed_quantity: "0",
        iqc_skipped_quantity: "0",
        pcb_full_panel_quantity: String(split.split.fullPanelBoards),
        pcb_partial_panel_quantity: String(split.split.partialPanelBoards),
        pcb_partial_panel_count: String(split.split.partialPanelCount),
      });
      return;
    }
    const validation = validateIqcQuantities(countedQuantity, fields);
    if (!validation.ok) {
      setError(validation.message);
      return;
    }
    setError(null);
    onSave({
      iqc_status: previewStatus,
      iqc_notes: notes.trim(),
      iqc_passed_quantity: passed.trim(),
      iqc_failed_quantity: failed.trim(),
      iqc_skipped_quantity: skipped.trim(),
      pcb_full_panel_quantity: "",
      pcb_partial_panel_quantity: "",
      pcb_partial_panel_count: "",
    });
  };

  return createPortal(
    <div {...hubModalOverlayProps(true)}>
      <Card className="mt-2 flex max-h-[min(94vh,56rem)] w-full max-w-5xl flex-col overflow-hidden shadow-xl lg:max-w-6xl">
        <CardHeader className="shrink-0 border-b border-slate-200">
          <h2 className="text-lg font-semibold text-slate-900">IQC for {sku || "line"}</h2>
          <p className="mt-1 text-sm text-slate-600">
            {pcbLine
              ? pcbIqcCheckInstruction()
              : description?.trim()
                ? description.trim()
                : "Record pass, fail, and skipped counts for this line."}{" "}
            {pcbLine ? "Quantity received" : "Counted on this receipt"}:{" "}
            <span className="font-semibold tabular-nums">{String(countedQuantity)}</span>{" "}
            {pcbLine ? `board${countedQuantity === 1 ? "" : "s"}` : `unit${countedQuantity === 1 ? "" : "s"}`}.
          </p>
          {previewSummary != null ? (
            <p className="mt-2 flex flex-wrap items-center gap-2 text-sm">
              <span
                className={cn(
                  "inline-flex rounded-md px-2 py-0.5 text-xs font-semibold uppercase tracking-wide ring-1",
                  inboundIqcChipClasses(previewStatus),
                )}
              >
                {previewSummary}
              </span>
              <span className="text-slate-500">Overall: {previewStatus}</span>
            </p>
          ) : null}
        </CardHeader>
        <CardBody className="flex min-h-0 flex-1 flex-col overflow-hidden p-0 lg:flex-row">
          <div className="flex min-h-[40vh] flex-1 flex-col border-b border-slate-200 lg:min-h-0 lg:max-w-[58%] lg:border-b-0 lg:border-r">
            <div className="flex min-h-0 flex-1 flex-col p-3 sm:p-4">
              <InboundIqcWikiPanel sku={sku} inline />
            </div>
          </div>

          <div className="flex min-h-0 flex-1 flex-col lg:max-w-[42%]">
            <div className="min-h-0 flex-1 overflow-y-auto p-4 sm:p-5">
              {pcbLine ? (
                <fieldset className="space-y-3">
                  <legend className="text-sm font-semibold text-slate-900">Panel check</legend>
                  <p className="text-sm text-slate-700">{pcbDirection}</p>
                  <div className="grid gap-3">
                    <label className="block">
                      <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                        Boards on full panels
                      </span>
                      <input
                        type="number"
                        inputMode="numeric"
                        min={0}
                        max={countedQuantity}
                        value={fullPanels}
                        disabled={labelsSaved}
                        onChange={(event) => setFullPanels(event.target.value)}
                        className="mt-1 h-11 w-full rounded-md border border-slate-300 px-3 text-lg tabular-nums focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                        aria-label="Boards on full panels"
                      />
                    </label>
                    <label className="block">
                      <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                        Boards on partial panels
                      </span>
                      <input
                        type="number"
                        inputMode="numeric"
                        min={0}
                        max={countedQuantity}
                        value={partialPanels}
                        disabled={labelsSaved}
                        onChange={(event) => setPartialPanels(event.target.value)}
                        className="mt-1 h-11 w-full rounded-md border border-slate-300 px-3 text-lg tabular-nums focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                        aria-label="Boards on partial panels"
                      />
                    </label>
                    <label className="block">
                      <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                        Partial panels
                      </span>
                      <input
                        type="number"
                        inputMode="numeric"
                        min={0}
                        value={partialPanelCount}
                        disabled={labelsSaved}
                        onChange={(event) => setPartialPanelCount(event.target.value)}
                        className="mt-1 h-11 w-full rounded-md border border-slate-300 px-3 text-lg tabular-nums focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                        aria-label="Number of partial panels"
                      />
                    </label>
                  </div>
                  <p className="text-xs text-slate-500">
                    Boards on full panels plus boards on partial panels must equal the quantity received (
                    {String(countedQuantity)}).
                  </p>
                </fieldset>
              ) : (
              <fieldset className="space-y-3">
                <legend className="text-sm font-semibold text-slate-900">Disposition</legend>
                <div className="grid gap-3 sm:grid-cols-3">
                  <label className="block">
                    <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Pass</span>
                    <input
                      type="number"
                      inputMode="numeric"
                      min={0}
                      max={countedQuantity}
                      value={passed}
                      onChange={(event) => setPassed(event.target.value)}
                      className="mt-1 h-11 w-full rounded-md border border-slate-300 px-3 text-lg tabular-nums focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                      aria-label="Pass quantity"
                    />
                  </label>
                  <label className="block">
                    <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Fail</span>
                    <input
                      type="number"
                      inputMode="numeric"
                      min={0}
                      max={countedQuantity}
                      value={failed}
                      onChange={(event) => setFailed(event.target.value)}
                      className="mt-1 h-11 w-full rounded-md border border-slate-300 px-3 text-lg tabular-nums focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                      aria-label="Fail quantity"
                    />
                  </label>
                  <label className="block">
                    <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Skipped</span>
                    <input
                      type="number"
                      inputMode="numeric"
                      min={0}
                      max={countedQuantity}
                      value={skipped}
                      onChange={(event) => setSkipped(event.target.value)}
                      className="mt-1 h-11 w-full rounded-md border border-slate-300 px-3 text-lg tabular-nums focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                      aria-label="Skipped quantity"
                    />
                  </label>
                </div>
                <p className="text-xs text-slate-500">
                  Pass + fail + skipped must equal the counted quantity ({String(countedQuantity)}).
                </p>
              </fieldset>
              )}

              <label className="mt-4 block">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">IQC notes (optional)</span>
                <div className="mt-1 flex gap-2">
                  <textarea
                    value={notes}
                    onChange={(event) => setNotes(event.target.value)}
                    rows={3}
                    placeholder="Defect notes, sample plan, hold reason…"
                    className="min-h-11 min-w-0 flex-1 rounded-md border border-slate-300 py-2 pl-3 pr-3 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                  />
                  <VoiceToTextMicButton
                    className="h-11 w-11 shrink-0"
                    value={notes}
                    ariaLabel="Dictate IQC notes"
                    onValueChange={setNotes}
                  />
                </div>
              </label>

              {error != null ? (
                <p className="mt-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800" role="alert">
                  {error}
                </p>
              ) : null}
            </div>

            <div className="flex shrink-0 flex-wrap justify-end gap-2 border-t border-slate-200 px-4 py-3 sm:px-5">
              <Button type="button" variant="secondary" disabled={saving} onClick={onCancel}>
                Cancel
              </Button>
              <Button type="button" variant="primary" disabled={saving || (pcbLine && !printerReady)} onClick={handleSave}>
                {saving ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : null}
                {pcbLine ? (labelsSaved ? "Print labels" : "Save IQC and print labels") : "Save IQC"}
              </Button>
            </div>
          </div>
        </CardBody>
      </Card>
    </div>,
    document.body,
  );
}

export function iqcQuantityFieldsFromLine(input: {
  passed: string | null | undefined;
  failed: string | null | undefined;
  skipped: string | null | undefined;
}): InboundIqcQuantityFields {
  return iqcQuantityFieldsFromNumbers({
    passed: input.passed != null && input.passed !== "" ? Number(input.passed) : null,
    failed: input.failed != null && input.failed !== "" ? Number(input.failed) : null,
    skipped: input.skipped != null && input.skipped !== "" ? Number(input.skipped) : null,
  });
}
